import React from 'react';
import { useQuoteStore } from '@/store/quoteStore';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Minus, Plus } from 'lucide-react';

const ANCILLARIES = [
  {
    id: 'trowel_lubricant',
    name: 'Trowel Lubricant/Cleaner',
    description: '5l',
  },
  {
    id: 'primer',
    name: '5l Driveway Primer',
    description: '25-30m² Coverage',
  },
  {
    id: 'crushed_glass',
    name: 'Crushed glass',
    description: '5kg',
  },
  {
    id: 'isopropanol',
    name: 'Isopropanol',
    description: '5l',
  },
  {
    id: 'quattromix',
    name: '120L Quattromix Forced Action Mixer',
    description: 'Hire or purchase — add here if you want it on the material order',
  },
];

const TRIM_ITEMS = [
  {
    id: 'rigid_trim',
    name: '18mm Rigid Trim',
    description: '2.5m lengths',
  },
  {
    id: 'formable_trim',
    name: '18mm Formable Trim',
    description: '2.5m lengths',
  },
];

const Step5Ancillaries = () => {
  const { ancillaries, updateAncillary, setCurrentStep, border } = useQuoteStore();

  const handleNext = () => {
    setCurrentStep(6);
  };

  const handleBack = () => {
    setCurrentStep(4);
  };

  const increment = (id) => {
    const current = ancillaries[id] || 0;
    updateAncillary(id, current + 1);
  };

  const decrement = (id) => {
    const current = ancillaries[id] || 0;
    if (current > 0) {
      updateAncillary(id, current - 1);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
      <h2 className="text-2xl md:text-3xl font-bold text-[#27292E] mb-2">
        Other Ancillaries
      </h2>
      <p className="text-gray-600 mb-8">
        Add any additional items to your order.
      </p>

      <div className="space-y-4">
        {ANCILLARIES.map((item) => (
          <div
            key={item.id}
            className="border-2 border-gray-200 rounded-xl p-5"
            data-testid={`ancillary-${item.id}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-[#27292E]">{item.name}</h3>
                <p className="text-sm text-gray-500">{item.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => decrement(item.id)}
                className="w-12 h-12 flex items-center justify-center bg-white border-2 border-[#2F5E96] text-[#2F5E96] rounded-lg hover:bg-[#2F5E96]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`${item.id}-decrement`}
                disabled={(ancillaries[item.id] || 0) <= 0}
              >
                <Minus className="w-5 h-5" />
              </button>

              <div className="flex-1 text-center">
                <div className="text-3xl font-bold text-[#2F5E96]" data-testid={`${item.id}-quantity`}>
                  {ancillaries[item.id] || 0}
                </div>
              </div>

              <button
                onClick={() => increment(item.id)}
                className="w-12 h-12 flex items-center justify-center bg-[#2F5E96] text-white rounded-lg hover:bg-[#254b7a] transition-colors"
                data-testid={`${item.id}-increment`}
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Trim Section */}
      <div className="mt-8">
        <h3 className="text-xl font-bold text-[#27292E] mb-2">Additional Trim</h3>
        {border && (border.rigid_trim > 0 || border.formable_trim > 0) && (
          <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg mb-4">
            Note: If you have added trim in the resin border calculator, trim added here will be extra.
          </p>
        )}
        <div className="space-y-4">
          {TRIM_ITEMS.map((item) => (
            <div
              key={item.id}
              className="border-2 border-gray-200 rounded-xl p-5"
              data-testid={`ancillary-${item.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-[#27292E]">{item.name}</h3>
                  <p className="text-sm text-gray-500">{item.description}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={() => decrement(item.id)}
                  className="w-12 h-12 flex items-center justify-center bg-white border-2 border-[#2F5E96] text-[#2F5E96] rounded-lg hover:bg-[#2F5E96]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`${item.id}-decrement`}
                  disabled={(ancillaries[item.id] || 0) <= 0}
                >
                  <Minus className="w-5 h-5" />
                </button>

                <div className="flex-1 text-center">
                  <div className="text-3xl font-bold text-[#2F5E96]" data-testid={`${item.id}-quantity`}>
                    {ancillaries[item.id] || 0}
                  </div>
                </div>

                <button
                  onClick={() => increment(item.id)}
                  className="w-12 h-12 flex items-center justify-center bg-[#2F5E96] text-white rounded-lg hover:bg-[#254b7a] transition-colors"
                  data-testid={`${item.id}-increment`}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-4 mt-8">
        <Button
          onClick={handleBack}
          variant="outline"
          className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/5"
          data-testid="back-button-step5"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleNext}
          className="flex-1 bg-[#2F5E96] hover:bg-[#254b7a] text-white h-14 text-lg font-bold rounded-xl transition-transform active:scale-95"
          data-testid="next-button-step5"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default Step5Ancillaries;
